import { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { assistantsApiService } from '../../services/assistantsApiService';
import DataTable, { type DataTableColumn } from '../data-display/DataTable';
import type { AssistantObject, CreateAssistantRequest } from '../../types/assistantsApi';

interface Props {
  agentSlug: string;
}

export const AssistantsList = ({ agentSlug }: Props) => {
  const [assistants, setAssistants] = useState<AssistantObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateAssistantRequest>({ model: 'gpt-4o', name: '', instructions: '' });

  useEffect(() => {
    loadAssistants();
  }, [agentSlug]);

  const loadAssistants = async () => {
    setLoading(true);
    try {
      const result = await assistantsApiService.listAssistants(agentSlug);
      setAssistants(result.data ?? []);
    } catch {
      setAssistants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await assistantsApiService.modifyAssistant(agentSlug, editingId, form);
      } else {
        await assistantsApiService.createAssistant(agentSlug, form);
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ model: 'gpt-4o', name: '', instructions: '' });
      loadAssistants();
    } catch (err) {
      console.error('Failed to save assistant', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await assistantsApiService.deleteAssistant(agentSlug, id);
      loadAssistants();
    } catch (err) {
      console.error('Failed to delete assistant', err);
    }
  };

  const openEdit = (a: AssistantObject) => {
    setEditingId(a.id);
    setForm({ model: a.model, name: a.name ?? '', instructions: a.instructions ?? '' });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ model: 'gpt-4o', name: '', instructions: '' });
    setDialogOpen(true);
  };

  const columns: DataTableColumn<AssistantObject>[] = [
    { id: 'id', label: 'ID', render: (r) => <Typography variant="body2" fontFamily="monospace" fontSize={12}>{r.id}</Typography> },
    { id: 'name', label: 'Name', render: (r) => r.name ?? '—' },
    { id: 'model', label: 'Model', render: (r) => <Chip label={r.model} size="small" variant="outlined" /> },
    {
      id: 'tools', label: 'Tools',
      render: (r) => r.tools?.length ?? 0,
    },
    {
      id: 'created_at', label: 'Created',
      render: (r) => new Date(r.created_at * 1000).toLocaleString(),
    },
    {
      id: 'actions', label: '', align: 'right',
      render: (r) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Manage assistants for this agent.
        </Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
          New Assistant
        </Button>
      </Box>

      <DataTable columns={columns} rows={assistants} loading={loading} emptyMessage="No assistants created yet." />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Assistant' : 'Create Assistant'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={form.name ?? ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Model"
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Instructions"
              value={form.instructions ?? ''}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              fullWidth
              multiline
              rows={4}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editingId ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
